// lib/api/notice.js
import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";

function resolveLocale(input) {
  const raw = String(
    input ||
      (typeof document !== "undefined" && document.documentElement?.lang) ||
      (typeof navigator !== "undefined" && navigator.language) ||
      "ko-KR",
  ).toLowerCase();

  if (raw.startsWith("en")) return "en-US";
  if (raw.startsWith("ja")) return "ja-JP";
  if (raw.startsWith("vi")) return "vi-VN";
  if (raw.startsWith("zh")) return "zh-CN";

  return "ko-KR";
}

/* ──────────────────────────────────────────────────────────────
 * 📦 getNoticeList (인증 필요)
 * ────────────────────────────────────────────────────────────── */
export async function getNoticeList({
  q = "",
  page = 1,
  limit = 12,
  locale,
  signal,
} = {}) {
  try {
    const params = new URLSearchParams();

    const qq = typeof q === "string" ? q.trim() : "";
    if (qq) params.set("q", qq);

    params.set("page", String(Number(page) || 1));
    params.set("limit", String(Number(limit) || 12));
    params.set("locale", resolveLocale(locale));

    const path = `${BACKEND_URL}/public/notices?${params.toString()}`;

    const res = await authFetch(
      path,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        signal, // ✅ 추가
      },
      { refreshUrlIncludes: "auth/refresh" },
    );

    if (!res.ok) {
      if (res.status === 401) {
        return {
          success: false,
          message: "인증이 만료되었습니다. 다시 로그인해 주세요.",
        };
      }
      console.error("❌ getNoticeList: 서버 응답 실패", res.status);
      return { success: false, message: "공지사항 목록 조회 실패" };
    }

    const json = await res.json().catch(() => null);
    if (!json?.success) {
      return {
        success: false,
        message: json?.message || "공지사항 목록 조회 실패",
      };
    }

    const data = json?.data ?? {};

    const totalCount = Number(
      data.totalCount ?? data.total ?? data.total_count ?? data.count ?? 0,
    );

    const items = data.items ?? data.notices ?? data.rows ?? data.list ?? [];

    return {
      success: true,
      totalCount: Number.isFinite(totalCount) ? totalCount : 0,
      items: Array.isArray(items) ? items : [],
    };
  } catch (err) {
    // ✅ abort는 조용히 처리 (원하면 메시지 변경 가능)
    if (err?.name === "AbortError")
      return { success: false, message: "ABORTED" };

    console.error("❌ getNoticeList 요청 실패:", err);
    return { success: false, message: "서버 요청 중 오류가 발생했습니다." };
  }
}

/* ──────────────────────────────────────────────────────────────
 * 📦 getNoticeDetail (인증 필요)
 *  - 기존 getNoticeDetail(id) 호출도 호환
 *  - 새로 getNoticeDetail({ id, signal }) 도 지원
 * ────────────────────────────────────────────────────────────── */
export async function getNoticeDetail(arg, opt = {}) {
  try {
    // ✅ 호환: getNoticeDetail("id") 또는 getNoticeDetail({ id, signal })
    const id = typeof arg === "object" ? arg?.id : arg;
    const signal = typeof arg === "object" ? arg?.signal : opt?.signal;
    const locale =
      typeof arg === "object"
        ? resolveLocale(arg?.locale)
        : resolveLocale(opt?.locale);

    const noticeId = String(id ?? "").trim();
    if (!noticeId) return { success: false, message: "공지 ID가 없습니다." };

    const params = new URLSearchParams();
    params.set("locale", locale);
    const path = `${BACKEND_URL}/public/notices_detail/${encodeURIComponent(noticeId)}?${params.toString()}`;

    const res = await authFetch(
      path,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        signal, // ✅ 추가
      },
      { refreshUrlIncludes: "auth/refresh" },
    );

    if (!res.ok) {
      if (res.status === 401) {
        return {
          success: false,
          message: "인증이 만료되었습니다. 다시 로그인해 주세요.",
        };
      }
      console.error("❌ getNoticeDetail: 서버 응답 실패", res.status);
      return { success: false, message: "공지사항 상세 조회 실패" };
    }

    const json = await res.json().catch(() => null);
    if (!json?.success) {
      return {
        success: false,
        message: json?.message || "공지사항 상세 조회 실패",
      };
    }

    const data = json?.data ?? {};
    const item = data.item ?? data.notice ?? data.row ?? null;

    return { success: true, item };
  } catch (err) {
    if (err?.name === "AbortError")
      return { success: false, message: "ABORTED" };

    console.error("❌ getNoticeDetail 요청 실패:", err);
    return { success: false, message: "서버 요청 중 오류가 발생했습니다." };
  }
}
